import {computed, type ComputedRef} from 'vue';
import {useUserStore} from '@/ts/store/user-store';
import {useApi} from '@/ts/composables/stateful/use-api';

/**
 * Drives the legal-docs sidebar hint (IPD-2517).
 *
 * The hint is a "show-once after the onboarding wizard" reminder pointing to
 * the legal-docs entry in the side panel. Whether it shows is encoded by a
 * single tri-state flag on the user (`legalDocsHintShown`):
 *
 *   - `null`  → user has never completed the wizard; the wizard itself
 *               covers the legal-docs location, no floating hint.
 *   - `false` → wizard completed, hint pending; render the hint, auto-dismiss
 *               after 5s (handled by SidebarTooltip), then call `dismiss()`.
 *   - `true`  → already shown; never render again.
 *
 * The transition `null → false` happens at wizard completion (see
 * `use-tour.ts → updateBEVersion`). The transition `false → true` happens
 * via `dismiss()` below.
 */
export function useLegalDocsHint(): {
  shouldShow: ComputedRef<boolean>;
  dismiss(): Promise<void>;
} {
  const userStore = useUserStore();
  const api = useApi();

  const shouldShow = computed(() => userStore.userData?.legalDocsHintShown === false);

  /**
   * Mark the hint as shown both locally and on the BE. Errors are swallowed:
   * the worst case is the hint appears again on the next login, which is
   * preferable to a retry storm or a noisy toast for a purely informational
   * UI element.
   */
  const dismiss = async(): Promise<void> => {
    if (userStore.userData?.legalDocsHintShown !== false) {
      return; // Nothing to do; avoids spurious PUTs when called defensively.
    }
    // Optimistically update local state so the hint hides immediately even if
    // the network call is slow.
    userStore.userData.legalDocsHintShown = true;
    try {
      await api.me.updateMe({legalDocsHintShown: true});
    } catch {
      // Intentionally silent — see JSDoc above.
    }
  };

  return {shouldShow, dismiss};
}
